// Email recipient type enum
export enum EmailRecipientType {
  TO = 'TO',
  CC = 'CC',
  BCC = 'BCC',
}

// Trust level enum
export enum TrustLevel {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

// Message receiver interface
export interface MessageInfoReceiver {
  email: string;
  type: EmailRecipientType;
}

export interface CmailMessage {
  agencyDomain: string;
  agencyName: string;
  attachments?: any[];
  childCount?: number;
  claimsNumber: string;
  dateReceived: string;
  emailDirection?: 'SENT' | 'INBOX';
  emailReferenceCount?: number;
  emailType?: string;
  entityTags: string;
  htmlBody: string;
  isArchived?: boolean;
  isStarred?: boolean;
  label?: string;
  labels?: string[];
  mainMessage: string;
  messageSentiment: string;
  msgCategory: string;
  msgId: string;
  origMessage?: string;
  phoneNumber: string;
  predictedAccountName: string;
  predictedJobPosition: string;
  predictedRetention: null;
  rank: number;
  receiverEmail: string;
  receiverName: string;
  references?: string[];
  seenBy?: string[];
  senderEmail: string;
  senderName: string;
  subject: string;
  assignedTo?: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface EnrichedCmailMessage extends CmailMessage {
  ui_dateReceivedLong: string;
  ui_dateReceivedShort: string;
  extractedData?: string;
  emailSubCategory?: string;
  emailSubSubCategory?: string[];
  label?: string;
  failureType?: string;
  jiraTicketNumber?: string;
}

export type MessageInfo = {
  attachments: MessageInfoAttachment[];
  /** Per-invoice PDFs cut from a multi-invoice attachment; each names its source in parentDocId. */
  invoiceSlices?: MessageInfoAttachment[];
  cbBodyEntities: MessageInfoCbBodyEntities;
  cbMessagePriority: MessageInfoCbMessagePriority;
  created: string;
  dateReceived: string;
  id: string;
  isRootMessage: boolean;
  label?: string;
  modified: string;
  msgCount: number;
  msgId: string;
  noOfAttachments: number;
  referenceEmails?: MessageInfoChild;
  references?: string[];
  senderEmail: string;
  senderName: string;
  senderFirstName?: string;
  senderLastName?: string;
  senderImageUrl?: string;
  state?: string;
  subject: string;
  textBodyAll: string;
  htmlBodyAll?: string;
  mainMessage?: string;
  origMessage?: string;
  userPreferences: MessageInfoUserPreferences;
  extractedData?: string;
  extractedDataJSON?: ExtractedDataJSON;
  senderTrustDecision?: SenderTrustDecision;
  failureType?: string;
  jiraTicketNumber?: string;
  headers?: Record<string, string>;
  receivers?: MessageInfoReceiver[];
  emailDirection?: 'SENT' | 'INBOX' | 'DRAFT';
  emailType?: string;
  msgCategory?: string;
  replyToMsgId?: string;
  assignedTo?: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
  warnings?: string[];
};

export type MessageInfoChild = Omit<
  MessageInfo,
  'referenceEmails' | 'userPreferences'
>;

export type MessageInfoAttachment = {
  bucketName: string;
  docContentType: string;
  docId: string;
  docName: string;
  docSize: string;
  docTime: string;
  docType: string;
  s3Path: string;
  signedS3Url: string;
  /** Audio attachments only: the AI-cleaned transcript. 403/404 until written. */
  transcriptSignedUrl?: string;
  /** Invoice slices only: the docId of the PDF this one was split from. */
  parentDocId?: string;
  /** Invoice slices only: the source pages this slice covers, e.g. "1-2". */
  pageRange?: string;
};

export type MessageInfoCbBodyEntities = {
  agencyDomain: string;
  agencyName: string;
  agent: MessageInfoAgent;
  predict?: PredictedInfo;
};

type MessageInfoAgent = {
  agencyId: string;
  agencyName: string;
  agentEmailAddress: string;
  agentId: string;
  locationId: string;
  locationName: string;
  teamId: string;
  teamName: string;
};

export type PredictedInfo = {
  agentEmailAddress: string;
  agentFirstName: string;
  agentLastName: string;
  agentName?: string;
  agencyName: string;
  deductible: number[];
  lineOfBusiness: string[];
  name: string;
  policyLimits: number[];
  state: string;
  teamName: string;
  totalRevenue: number;
};

export type MessageInfoCbMessagePriority = {
  priority: number;
  tags: string[];
};

export type MessageInfoUserPreferences = {
  isArchived: boolean;
  isStarred: boolean;
  seenBy: string[];
};

export interface SenderTrustDecision {
  trustLevel: TrustLevel;
  alignedDomain: string;
  reasons: string[];
  flags: {
    valid_mx_records: boolean;
    dkim_pass: boolean;
    reply_to_divergent: boolean;
    return_path_divergent: boolean;
    spf_pass: boolean;
    tls_used: boolean;
    valid_a_records: boolean;
    lookalike_detected: boolean;
    dmarc_pass: boolean;
    suspicious_helo: boolean;
  };
  assessedAt: string;
}

export interface ExtractedDataJSON {
  senderDetails?: {
    userVerified?: boolean;
    [key: string]: any;
  };
  // validatedUser?: boolean; // Deprecated: use senderDetails.userVerified instead
  [key: string]: any;
}
